import React, { useEffect, useState } from "react";
import { Modal, Form, notification, Upload, Row, Col, Input } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import config from "../../../../config/config";

const Edit = ({ isVisible, handleClose, moduleName, base_url, record }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const azure_base = config.azure_object_url || "";
  const sizeLimit = config.sizeLimit;
  const allowedExtensions = config.allowed_extensions;

  const uploadProps = {
    beforeUpload: () => false,
    maxCount: 1,
    listType: "picture-card",
    showUploadList: { showPreviewIcon: false, showRemoveIcon: true },
  };

  // ---------------- LOAD EXISTING DATA ----------------
  useEffect(() => {
    if (record) {
      form.setFieldsValue({
        initial: record.initial,
        title: record.title,
        desc: record.desc,
        bgImg: record.bgImg || [],
        mbBgImg: record.mbBgImg || [],
        icon: record.icon || [],
      });
    }
  }, [record, form]);

  // ---------------- SUBMIT ----------------
  const handleSubmit = async (values) => {
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("id", record._id);
      formData.append("initial", values.initial);
      formData.append("title", values.title);
      formData.append("desc", values.desc);

      if (values.bgImg?.[0]?.originFileObj)
        formData.append("bgImgWeb", values.bgImg[0].originFileObj);
      if (values.mbBgImg?.[0]?.originFileObj)
        formData.append("bgImgMobile", values.mbBgImg[0].originFileObj);
      if (values.icon?.[0]?.originFileObj)
        formData.append("icon", values.icon[0].originFileObj);

      const res = await axios.post(`${base_url}updateCareerValue`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      notification.success({
        message: res.data.message || `${moduleName} updated successfully`,
      });
      handleClose(true);
    } catch (err) {
      notification.error({
        message: err?.response?.data?.message || "Update failed",
      });
    } finally {
      setLoading(false);
    }
  };

  const renderUploadField = (fieldName, label, extraText) => {
    const currentFile = form.getFieldValue(fieldName);

    return (
      <Form.Item
        label={label}
        name={fieldName}
        valuePropName="fileList"
        extra={extraText}
        getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList || [])}
      >
        <Upload
          {...uploadProps}
          fileList={currentFile || []}
          accept=".jpg,.jpeg,.png,.svg" // <-- restrict file types
          beforeUpload={(file) => {
            const allowedExtensions = ["jpg", "jpeg", "png", "svg"];
            const ext = file.name.split(".").pop().toLowerCase();
            if (!allowedExtensions.includes(ext)) {
              notification.error({
                message: "Invalid file format",
                description: "Only JPG, PNG, or SVG files are allowed",
              });
              return Upload.LIST_IGNORE; // prevent file from being added
            }
            return false; // keep file in Upload list
          }}
          onChange={({ fileList }) =>
            form.setFieldsValue({ [fieldName]: fileList })
          }
        >
          <div>
            <PlusOutlined />
          </div>
        </Upload>
      </Form.Item>
    );
  };

  return (
    <Modal
      title={`Edit ${moduleName}`}
      open={isVisible}
      onCancel={() => handleClose(false)}
      okText="Update"
      cancelText="Cancel"
      confirmLoading={loading}
      width={700}
      onOk={() => form.validateFields().then(handleSubmit)}
    >
      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            {renderUploadField(
              "bgImg",
              "Desktop Background Image",
              "Recommended size: 310 × 369"
            )}
          </Col>
          <Col xs={24} md={8}>
            {renderUploadField(
              "mbBgImg",
              "Mobile Background Image",
              "Recommended size: 335 × 218"
            )}
          </Col>
          <Col xs={24} md={8}>
            {renderUploadField("icon", "Icon", "Recommended size: 48 × 46")}
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Initial" name="initial">
              <Input maxLength={1} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Title"
              name="title"
              extra="Recommended character limit: 18"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              label="Description"
              name="desc"
              extra="Recommended character limit: 150"
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default Edit;
